
import React, { useState } from 'react';
import { Shield, AlertTriangle, CheckCircle, Download, FileText, Sparkles, ClipboardList, Flag, Save, Trash2 } from 'lucide-react';
import { generateChecklist, detectRedFlags, exportCSV, type ChecklistItem } from './lib/engine';
import { aiRiskAssessment } from './lib/ai-fallback';
import { SettingsButton } from './lib/SettingsButton';

export default function App() {
  const [jurisdiction, setJurisdiction] = useState('Canada');
  const [sector, setSector] = useState('banking');
  const [riskLevel, setRiskLevel] = useState('Medium');
  const [entityType, setEntityType] = useState('Individual');
  const [includeMonitoring, setIncludeMonitoring] = useState(true);
  const [includeTraining, setIncludeTraining] = useState(true);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [clientDesc, setClientDesc] = useState('');
  const [redFlags, setRedFlags] = useState<ReturnType<typeof detectRedFlags>>([]);
  const [aiAssessment, setAiAssessment] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [savedProfiles, setSavedProfiles] = useState(() => JSON.parse(localStorage.getItem('ca_compliance') || '[]'));

  const generate = () => {
    const cl = generateChecklist(jurisdiction, sector, riskLevel, entityType, includeMonitoring, includeTraining);
    setChecklist(cl);
    if (clientDesc.trim()) setRedFlags(detectRedFlags(clientDesc));
    setAiAssessment('');
  };

  const updateStatus = (id: string, status: string) => setChecklist(prev => prev.map(c => c.id === id ? { ...c, status } : c));
  const updateNotes = (id: string, notes: string) => setChecklist(prev => prev.map(c => c.id === id ? { ...c, notes } : c));

  const handleAiAssessment = async () => {
    if (!clientDesc.trim() || checklist.length === 0) return;
    setIsAiLoading(true); setAiAssessment('');
    await aiRiskAssessment(clientDesc, checklist, c => setAiAssessment(p => p + c));
    setIsAiLoading(false);
  };

  const saveProfile = () => {
    const profile = { id: Date.now().toString(), name: `${jurisdiction} — ${sector} — ${entityType}`, jurisdiction, sector, riskLevel, entityType, checklist, clientDesc, redFlags, date: new Date().toLocaleDateString() };
    const n = [profile, ...savedProfiles]; setSavedProfiles(n); localStorage.setItem('ca_compliance', JSON.stringify(n));
  };

  const loadProfile = (p: any) => {
    setJurisdiction(p.jurisdiction); setSector(p.sector); setRiskLevel(p.riskLevel); setEntityType(p.entityType);
    setChecklist(p.checklist); setClientDesc(p.clientDesc || ''); setRedFlags(p.redFlags || []);
  };

  const exportData = () => {
    const csv = exportCSV(checklist);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `compliance-checklist-${Date.now()}.csv`; a.click();
  };

  const statusOptions = ['', 'Not Started', 'In Progress', 'Complete', 'N/A'];
  const riskColor = (w: string) => w === 'Critical' ? 'text-red-400 bg-red-500/10 border-red-500/30' : w === 'Important' ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' : 'text-green-400 bg-green-500/10 border-green-500/30';

  return (
    <div className="min-h-screen bg-[#050b14] text-[#f0f0f0]">
      <header className="border-b border-white/10 bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#d4af37] flex items-center justify-center text-[#0a1628] font-bold text-lg">C</div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-[#f0f0f0]">CatalystAgile <span className="text-[#d4af37]">KYC/AML Checklist</span></h1>
              <p className="text-xs text-[#8a9bb8]">FATF/FINTRAC Framework Engine + AI Risk Assessment</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {checklist.length > 0 && (
              <>
                <button onClick={saveProfile} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 text-[#8a9bb8] transition-colors flex items-center gap-1.5"><Save className="w-3.5 h-3.5"/> Save</button>
                <button onClick={exportData} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 text-[#8a9bb8] transition-colors flex items-center gap-1.5"><Download className="w-3.5 h-3.5"/> CSV</button>
              </>
            )}
            <SettingsButton className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 inline-flex items-center gap-1.5 transition-colors text-[#8a9bb8] hover:text-[#d4af37]" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: Config */}
        <aside className="lg:col-span-3 space-y-3">
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4 space-y-3">
            <h2 className="text-sm font-semibold text-[#d4af37] flex items-center gap-2"><ClipboardList className="w-4 h-4"/> Configuration</h2>
            <div><label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">Jurisdiction</label>
              <select value={jurisdiction} onChange={e => setJurisdiction(e.target.value)} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50">
                <option>Canada</option><option>USA</option><option>UK</option><option>EU</option><option>Global</option>
              </select>
            </div>
            <div><label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">Sector</label>
              <select value={sector} onChange={e => setSector(e.target.value)} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50">
                <option value="banking">Banking</option><option value="msb">MSB</option><option value="securities">Securities</option><option value="fintech">Fintech</option><option value="crypto">Crypto</option><option value="insurance">Insurance</option>
              </select>
            </div>
            <div><label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">Risk Level</label>
              <select value={riskLevel} onChange={e => setRiskLevel(e.target.value)} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50">
                <option>Low</option><option>Medium</option><option>High</option><option>PEP</option><option>Sanctions</option>
              </select>
            </div>
            <div><label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">Entity Type</label>
              <select value={entityType} onChange={e => setEntityType(e.target.value)} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50">
                <option>Individual</option><option>Corporate</option><option>Trust</option><option>NGO</option><option>PEP</option>
              </select>
            </div>
            <div className="flex items-center justify-between"><label className="text-xs text-[#f0f0f0]">Include Monitoring</label><input type="checkbox" checked={includeMonitoring} onChange={e => setIncludeMonitoring(e.target.checked)} className="w-4 h-4 accent-[#d4af37]"/></div>
            <div className="flex items-center justify-between"><label className="text-xs text-[#f0f0f0]">Include Training</label><input type="checkbox" checked={includeTraining} onChange={e => setIncludeTraining(e.target.checked)} className="w-4 h-4 accent-[#d4af37]"/></div>
            <button onClick={generate} className="w-full px-4 py-2.5 rounded-lg bg-[#d4af37] text-[#0a1628] font-semibold text-sm hover:bg-[#e8c547] transition-colors flex items-center justify-center gap-1.5"><Shield className="w-4 h-4"/> Generate Checklist</button>
          </div>

          {/* Saved Profiles */}
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Save className="w-4 h-4"/> Saved Profiles ({savedProfiles.length})</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {savedProfiles.map((p: any) => (
                <button key={p.id} onClick={() => loadProfile(p)} className="w-full text-left bg-[#132238] rounded-lg p-2.5 border border-white/5 hover:border-[#d4af37]/30 transition-colors">
                  <div className="text-xs font-medium text-[#f0f0f0]">{p.name}</div>
                  <div className="text-[10px] text-[#8a9bb8] mt-0.5">{p.date} · {p.checklist.length} items</div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* CENTER: Results */}
        <section className="lg:col-span-9 space-y-4">
          {/* Red Flag Detector */}
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#f0f0f0] mb-3 flex items-center gap-2"><Flag className="w-4 h-4 text-[#d4af37]"/> Red Flag Detector</h2>
            <textarea value={clientDesc} onChange={e => setClientDesc(e.target.value)}
              placeholder="Describe client or transaction scenario to detect red flags..."
              rows={3} className="w-full bg-[#070f1a] border border-white/10 rounded-lg p-3 text-sm text-[#f0f0f0] placeholder-[#8a9bb8]/50 focus:outline-none focus:border-[#d4af37]/50 resize-none mb-3"/>
            {redFlags.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {redFlags.map((f, i) => (
                  <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-3.5 h-3.5 text-red-400"/><span className="text-[10px] uppercase tracking-wider text-red-400 font-semibold">{f.category.replace('_', ' ')}</span></div>
                    <p className="text-xs text-[#f0f0f0]">{f.indicator}</p>
                  </div>
                ))}
              </div>
            )}
            {clientDesc && redFlags.length === 0 && <p className="text-xs text-green-400 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5"/> No red flags detected based on known patterns.</p>}
          </div>

          {/* AI Risk Assessment */}
          {checklist.length > 0 && (
            <div className="bg-[#0f1d32] rounded-xl border border-[#d4af37]/20 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-[#d4af37] flex items-center gap-2"><Sparkles className="w-4 h-4"/> AI Risk Assessment</h2>
                <button onClick={handleAiAssessment} disabled={isAiLoading} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-[#d4af37]/30 text-[#d4af37] transition-colors disabled:opacity-50">
                  {isAiLoading ? 'Analyzing...' : 'Generate'}
                </button>
              </div>
              <div className="bg-[#070f1a] rounded-lg p-4 text-sm text-[#f0f0f0] whitespace-pre-wrap border border-white/10 min-h-[100px]">{aiAssessment || 'Click Generate to create an AI-powered risk assessment narrative based on the client description and checklist.'}</div>
            </div>
          )}

          {/* Checklist */}
          {checklist.length > 0 && (
            <div className="bg-[#0f1d32] rounded-xl border border-white/10 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-[#f0f0f0] flex items-center gap-2"><ClipboardList className="w-4 h-4"/> Compliance Checklist ({checklist.length} items)</h2>
                <div className="flex items-center gap-3 text-xs text-[#8a9bb8]">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"/> Critical</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"/> Important</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"/> Standard</span>
                </div>
              </div>
              <div className="divide-y divide-white/5">
                {checklist.map(item => (
                  <div key={item.id} className="px-4 py-3 hover:bg-[#132238]/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5"><span className={`text-[10px] px-1.5 py-0.5 rounded border ${riskColor(item.riskWeight)}`}>{item.riskWeight}</span></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-[#8a9bb8] mb-0.5">{item.pillar} · {item.applicability}</div>
                        <div className="text-sm text-[#f0f0f0] mb-2">{item.requirement}</div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-[#8a9bb8]">Evidence: <span className="text-[#f0f0f0]">{item.evidence}</span></span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <select value={item.status} onChange={e => updateStatus(item.id, e.target.value)} className="bg-[#070f1a] border border-white/10 rounded px-2 py-1 text-xs text-[#f0f0f0] focus:outline-none focus:border-[#d4af37]/50">
                          {statusOptions.map(s => <option key={s} value={s}>{s || 'Select...'}</option>)}
                        </select>
                      </div>
                    </div>
                    <input value={item.notes} onChange={e => updateNotes(item.id, e.target.value)} placeholder="Notes..." className="w-full mt-2 bg-[#070f1a] border border-white/10 rounded px-2 py-1 text-xs text-[#f0f0f0] placeholder-[#8a9bb8]/50 focus:outline-none focus:border-[#d4af37]/50"/>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
